import type { FC } from 'react';
import React from 'react';
import { isEmpty } from 'lodash';

import styled from 'styled-components';
import { useTranslation } from '@lt/localization';
import ShowReviewsButton from '../../containers/Reviews/ShowReviewsButton';
import Metapolicies from '../../../../components/Metapolicies';

import type { IAboutHotel } from './types';

const AboutHotel: FC<IAboutHotel> = ({
  text,
  airports,
  metapolicies,
  isMobile,
  showReviewsButton,
  hotelName,
}) => {
  const { t } = useTranslation();
  const hasMetapolicies = !isEmpty(metapolicies);
  const hasAirpots = !isEmpty(airports);
  const hasContent = Boolean(text) || hasAirpots || hasMetapolicies;

  if (!hasContent) return null;

  return (
    <div>
      <StyledTitle isMobile={isMobile}>{`${t(
        'hotel.aboutHotel.title',
      )} ${hotelName}`}</StyledTitle>
      {text ? (
        <StyledDescription isMobile={isMobile}>{text}</StyledDescription>
      ) : null}

      {hasAirpots ? (
        <>
          <StyledSubTitle isMobile={isMobile}>
            {t('hotel.aboutHotel.airports')}
          </StyledSubTitle>
          <StyledList>
            {airports?.map(({ name, distance }) => (
              <li key={`${name}_${distance}`}>{`${name} — ${distance} км`}</li>
            ))}
          </StyledList>
        </>
      ) : null}

      {hasMetapolicies ? (
        <Metapolicies metapolicies={metapolicies} isMobile={isMobile} />
      ) : null}

      {showReviewsButton ? <ShowReviewsButton /> : null}
    </div>
  );
};

const StyledSubTitle = styled.h5<{ isMobile?: boolean }>`
  margin: 0;
  text-transform: uppercase;
  color: ${(props) => props.theme.colors.blackBase};
  ${(props) => props.theme.typography.headline6}
`;

const StyledList = styled.ul`
  margin-top: 8px;
  li {
    margin-bottom: 4px;
  }
`;

const StyledTitle = styled.h2<{ isMobile?: boolean }>`
  padding-bottom: 5px;
  ${(props) => props.theme.typography.headline2}
  color: ${(props) => props.theme.colors.blackBase};

  ${(props) =>
    props.isMobile &&
    `
    ${props.theme.typography.headline3}
  `}
`;

const StyledDescription = styled.p<{ isMobile?: boolean }>`
  margin-right: 100px;
  margin-bottom: 20px;
  white-space: pre-line;
  ${(props) => props.theme.text1}
  color: ${(props) => props.theme.colors.blackLight};

  ${(props) =>
    props.isMobile &&
    `
    ${props.theme.typography.t1s}
    margin-right: 30px;
  `}
`;

export default AboutHotel;
